import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, makeStyles, mergeClasses, tokens, Spinner } from '@fluentui/react-components';
import { CheckmarkCircleFilled, PersonRegular } from '@fluentui/react-icons';
import type {
    SearchResultDto,
    SearchTaskDto,
    SearchProjectDto,
    ProjectDtoV2,
    TaskDtoV2,
    SearchScope,
} from '../../../../api/generated/v2/generated/data-contracts';
import { useGridStore } from '../../state';
import shallow from 'zustand/shallow';

type TaskSearchResultsProps = {
    data?: SearchResultDto[];
    isLoading: boolean;
    isFetchingNextPage: boolean;
    hasNextPage?: boolean;
    scope: SearchScope;
    onFetchNextPage: () => void;
    onTaskAdded: (taskId: string, projectId: string) => void;
};

const SCROLL_THRESHOLD = 50;

const useStyles = makeStyles({
    container: {
        maxHeight: '360px',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    projectHeader: {
        paddingTop: '8px',
        paddingBottom: '4px',
        paddingLeft: '8px',
        paddingRight: '8px',
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    taskButton: {
        width: '100%',
        justifyContent: 'flex-start',
        fontWeight: tokens.fontWeightRegular,
        minHeight: '36px',
    },
    taskContent: {
        display: 'flex',
        alignItems: 'center',
        columnGap: '8px',
        width: '100%',
    },
    taskName: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'left',
    },
    taskNameInGrid: {
        color: tokens.colorNeutralForeground3,
    },
    assignedIcon: {
        color: tokens.colorBrandForeground1,
        flexShrink: 0,
    },
    inGridIcon: {
        color: tokens.colorPaletteGreenForeground1,
        flexShrink: 0,
    },
    emptyMessage: {
        paddingTop: '16px',
        paddingBottom: '16px',
        textAlign: 'center',
        color: tokens.colorNeutralForeground3,
    },
    spinnerContainer: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '16px',
        paddingBottom: '16px',
    },
    loadMoreSpinner: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '8px',
        paddingBottom: '8px',
    },
});

export const TaskSearchResults = memo(function TaskSearchResults({
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    scope,
    onFetchNextPage,
    onTaskAdded,
}: TaskSearchResultsProps) {
    const styles = useStyles();
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        containerRef.current?.scrollTo(0, 0);
    }, [scope]);

    const { tasks, projects, addTask, addProject, setProjectExpanded } = useGridStore(
        s => ({
            tasks: s.tasks,
            projects: s.projects,
            addTask: s.addTask,
            addProject: s.addProject,
            setProjectExpanded: s.setProjectExpanded,
        }),
        shallow,
    );

    const gridTaskIds = useMemo(() => new Set(tasks.map(t => t.id)), [tasks]);
    const gridProjectIds = useMemo(() => new Set(projects.map(p => p.id)), [projects]);

    const { mergedTasks, mergedProjects } = useMemo(() => {
        if (!data?.length) return { mergedTasks: [] as SearchTaskDto[], mergedProjects: [] as SearchProjectDto[] };

        const projectMap = new Map<string, SearchProjectDto>();
        const taskMap = new Map<string, SearchTaskDto>();

        for (const page of data) {
            for (const project of page.projects ?? []) {
                projectMap.set(project.id, project);
            }
            for (const task of page.tasks ?? []) {
                taskMap.set(task.id, task);
            }
        }

        return { mergedTasks: Array.from(taskMap.values()), mergedProjects: Array.from(projectMap.values()) };
    }, [data]);

    const groupedTasks = useMemo(() => {
        if (!mergedTasks.length || !mergedProjects.length) return [];

        const projectMap = new Map<string, SearchProjectDto>();
        for (const project of mergedProjects) {
            projectMap.set(project.id, project);
        }

        const groups = new Map<string, { project: SearchProjectDto; tasks: SearchTaskDto[] }>();
        for (const task of mergedTasks) {
            const project = projectMap.get(task.projectId);
            if (!project) continue;

            const existing = groups.get(task.projectId);
            if (existing) {
                existing.tasks.push(task);
            } else {
                groups.set(task.projectId, { project, tasks: [task] });
            }
        }

        return Array.from(groups.values());
    }, [mergedTasks, mergedProjects]);

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            if (!hasNextPage || isFetchingNextPage) return;
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
                onFetchNextPage();
            }
        },
        [hasNextPage, isFetchingNextPage, onFetchNextPage],
    );

    const handleTaskClick = useCallback(
        (searchTask: SearchTaskDto, searchProject: SearchProjectDto) => {
            const isInGrid = gridTaskIds.has(searchTask.id);

            if (isInGrid) {
                setProjectExpanded(searchTask.projectId, true);
                onTaskAdded(searchTask.id, searchTask.projectId);
                return;
            }

            if (!gridProjectIds.has(searchTask.projectId)) {
                const newProject: ProjectDtoV2 = {
                    id: searchProject.id,
                    name: searchProject.name,
                };
                addProject(newProject);
            }

            const newTask: TaskDtoV2 = {
                id: searchTask.id,
                taskName: searchTask.name,
                projectId: searchTask.projectId,
                pinned: false,
                columns: {
                    totalWork: searchTask.plannedWork ?? 0,
                    remainingWork: searchTask.remainingWork ?? 0,
                    actualWork: searchTask.actualWork ?? 0,
                },
            };
            addTask(newTask);
            setProjectExpanded(searchTask.projectId, true);
            onTaskAdded(searchTask.id, searchTask.projectId);
        },
        [gridTaskIds, gridProjectIds, addTask, addProject, setProjectExpanded, onTaskAdded],
    );

    if (isLoading) {
        return (
            <div className={styles.spinnerContainer}>
                <Spinner size="small" />
            </div>
        );
    }

    if (!data?.length || (mergedTasks.length === 0 && mergedProjects.length === 0)) {
        return <div className={styles.emptyMessage}>No results</div>;
    }

    return (
        <div ref={containerRef} className={styles.container} onScroll={handleScroll} data-testid="search-results-container">
            {groupedTasks.map(({ project, tasks: groupTasks }) => (
                <div key={project.id}>
                    <div className={styles.projectHeader}>{project.name}</div>
                    {groupTasks.map(task => {
                        const isInGrid = gridTaskIds.has(task.id);
                        return (
                            <Button key={task.id} className={styles.taskButton} appearance="subtle" onClick={() => handleTaskClick(task, project)}>
                                <div className={styles.taskContent}>
                                    {task.isAssigned && <PersonRegular className={styles.assignedIcon} fontSize={14} />}
                                    <span className={mergeClasses(styles.taskName, isInGrid && styles.taskNameInGrid)}>{task.name}</span>
                                    {isInGrid && <CheckmarkCircleFilled className={styles.inGridIcon} fontSize={14} />}
                                </div>
                            </Button>
                        );
                    })}
                </div>
            ))}
            {isFetchingNextPage && (
                <div className={styles.loadMoreSpinner}>
                    <Spinner size="tiny" />
                </div>
            )}
        </div>
    );
});
